import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon, UsersIcon, ArrowUpIcon, ArrowDownIcon,
  Calendar, BarChart3, PieChart as PieChartIcon, X, Lock, Send, Info,
  AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';
import { getDashboardAnalytics, getSellerRecommendationsForDashboard, getSellerRecommendationsSent, getSellerRecommendationsHide, addSellerRecommendationStatus, checkSubscriptionStatus, createBuyerDemo } from '../../utils/api';
import useSubscriberStatus from '../../hooks/useSubscriberStatus';
import ScrollToTop from '../../components/layout/ScrollToTop';

const ITEMS_PER_PAGE = 10;
const imageUrl = process.env.REACT_APP_IMAGE_URL;

const ProfileDialog = ({ isOpen, onClose, profile }) => {
  if (!isOpen || !profile) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold">Profile Details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-4">User Information</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Name</label>
                  <p className="font-medium">{profile.first_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Source</label>
                  <p className="font-medium">{profile.source}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Date</label>
                  <p className="font-medium">{new Date(profile.date_created).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company Information</h4>
              <div className="space-y-3">
                <div className="mb-4">
                  <img
                    src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + profile.logo}
                    alt="Company logo"
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-500">Company Name</label>
                  <p className="font-medium">{profile.buyer_company_name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, onClose, onConfirm, lead }) => {
  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-purple-500 mr-2" />
            <h3 className="text-xl font-semibold">Confirm Invitation</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Send className="w-5 h-5 text-purple-500 mt-1" />
            </div>
            <div>
              <p className="text-gray-700">
                Send a demo invitation to <span className="font-semibold">{lead.first_name} {lead.last_name}</span> at <span className="font-semibold">{lead.company_name}</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This will use 1 credit from your account.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(lead);
              onClose();
            }}
            className="px-6 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 font-medium transition-colors inline-flex items-center"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Confirm & Send
          </button>
        </div>
      </div>
    </div>
  );
};

const UpgradeDialog = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upgrade Required</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Upgrade your account to invite potential buyers to product demos. This feature is available only for premium subscribers.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // TODO: Add upgrade flow navigation
              navigate('/subscription');
              onClose();
            }}
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};


const Recommendations = () => {

  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsSent, setRecommendationsSent] = useState([]);
  const [recommendationsHide, setRecommendationsHide] = useState([]); const [activeLeadsTab, setActiveLeadsTab] = useState('available'); // Add new state for leads tab
  const [recommendationsDisplayCount, setRecommendationsDisplayCount] = useState(ITEMS_PER_PAGE); // Add this state
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const { subscriberStatus, subscriberLoading, subscriberData, subscriberError, refetch } = useSubscriberStatus();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loadedSubscriptionCheck, setLoadedSubscriptionCheck] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [companyClicks, setCompanyClicks] = useState([]);
  const [loadedRecommendations, setLoadedRecommendations] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(5); // Add this state
  const [totalCount, setTotalCount] = useState(0); // Add this state
  const [currentPage, setCurrentPage] = useState(1); // Add this state
  const [totalPages, setTotalPages] = useState(0);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = React.useState(null);
  const [companyViews, setCompanyViews] = React.useState(null);
  const [tableData, setTableData] = React.useState([]);

  async function fetchAnalyticsData() {
    const response = await getDashboardAnalytics();
    if (response.success) {
      setAnalyticsData(response.data);
      setCompanyClicks(response.data.company_clicks);
      setCompanyViews(response.data.company_views);
      setTableData(
        response.data.company_table_data.map(row => ({
          ...row,
          view_count: parseInt(row.view_count, 10),
          click_count: parseInt(row.click_count, 10),
          impression_count: parseInt(row.impression_count, 10),
          year: row.year // Add year from date
        }))
      );

      setLoading(false);
    } else {
      console.error('Failed to fetch analytics data:', response.message);
    }
  }

  useEffect(() => {
    async function fetchRecommendationsSent() {
      const response = await getSellerRecommendationsSent();
      if (response.success) {
        setRecommendationsSent(response.data);
        setLoadedRecommendations(true);
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
    }

    async function fetchRecommendationsHide() {
      const response = await getSellerRecommendationsHide();
      if (response.success) {
        setRecommendationsHide(response.data);
        setLoadedRecommendations(true);
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
    }

    async function fetchRecommendations() {
      setRecommendationsLoading(true);

      const response = await getSellerRecommendationsForDashboard();
      if (response.success) {
        setRecommendations(response.data);
        setTotalCount(response.total); // Set the total count of recommendations
        setLoadedRecommendations(true);

        setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE)); // Calculate total pages based on items per page
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
      setRecommendationsLoading(false);
    }

    async function loadSubscriptionCheck() {
      let res = await checkSubscriptionStatus();

      if (res.success) {
        setHasSubscription(res.data);
        setLoadedSubscriptionCheck(true);
      }
    }

    fetchRecommendations();
    fetchRecommendationsSent();
    fetchRecommendationsHide();
    loadSubscriptionCheck();
  }, []);

  const handleInviteToDemo = (lead) => {
    if (!hasSubscription) {
      setIsUpgradeDialogOpen(true);
      return;
    }

    setSelectedInvite(lead);
    setIsConfirmDialogOpen(true);
  };

  async function handleBuyerNoMatch(id) {
    let res = await addSellerRecommendationStatus(id, 'Hide', 'Seller dashboard recommendation');

    if (res.success) {
      // Update recommendations and hide lists
      await fetchRecommendations(currentPage, searchTerm);
      await fetchRecommendationsHide();

      // Also remove from companyClicks
      setCompanyClicks(prev => prev.filter(click => click.registered_user_id !== id));
    }
  }

  async function fetchRecommendations(page = 1, search = '') {
    setRecommendationsLoading(true);
    const response = await getSellerRecommendationsForDashboard(page, search);
    if (response.success) {
      setRecommendations(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
    setRecommendationsLoading(false);
  }

  async function fetchRecommendationsSent() {
    const response = await getSellerRecommendationsSent();
    if (response.success) {
      setRecommendationsSent(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
  }

  async function fetchRecommendationsHide() {
    const response = await getSellerRecommendationsHide();
    if (response.success) {
      setRecommendationsHide(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
  }

  const handlePageChange = async (page) => {
    if (!hasSubscription && page > 1) {
      setIsUpgradeDialogOpen(true);
      return;
    }

    let res = await getSellerRecommendationsForDashboard(page);

    if (res.success) {
      setRecommendations(res.data);
      setCurrentPage(page);
    }
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 rounded ${currentPage === i
            ? 'bg-purple-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          First
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Next
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Last
        </button>
      </div>
    );
  };

  const filterRecommendations = (recommendations) => {
    return recommendations;

    return recommendations.filter(lead =>
      lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  async function handleConfirmInvite(lead) {
    try {
      let res = await addSellerRecommendationStatus(lead.registered_user_id, 'Approved', 'Seller dashboard recommendation');

      if (res.success) {
        let data2 = { buyer_id: lead.registered_user_id, credit_cost: 5, message: 'Booked through dashboard recommendations' }
        await createBuyerDemo(data2);
      }

      setSuccessMessage(`Introduction request sent successfully to ${lead.first_name} ${lead.last_name}`);
      setIsSuccessDialogOpen(true);

      fetchAnalyticsData();
      setAvailableCredits(prev => prev - 1);

      // Refresh the current page of recommendations
      await fetchRecommendations(currentPage, searchTerm);
      await fetchRecommendationsSent();
    } catch (error) {
      console.error('Failed to send intro:', error);
    }
  }

  async function handleSearch() {
    //alert('Search term: ' + searchTerm);
    setCurrentPage(1); // Reset to first page on search
    await fetchRecommendations(1, searchTerm);
  }

  return (
    <>
      <ScrollToTop />
      <div>
        {/* Add new Recommended Leads section */}
        <div className="rounded-lg border bg-white dark:bg-dark shadow-sm mb-8">
          <div className="p-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Recommended Leads</h3>
              <p className="text-sm text-gray-500">Potential leads that match your business profile</p>
            </div>

          </div>

          <div className="px-6 pb-4">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Search by name, company, title, or industry..."
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => handleSearch()}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="border-b px-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveLeadsTab('available')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'available'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Available to Send
              </button>
              <button
                onClick={() => setActiveLeadsTab('sent')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'sent'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Already Sent
              </button>

              <button
                onClick={() => setActiveLeadsTab('notsent')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'notsent'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Not a Fit
              </button>
            </div>
          </div>

          <div className="p-6 pt-0">
            <div className="w-full overflow-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="border-b">
                    <th></th>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Company</th>
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Industry</th>
                    <th className="text-left p-4 font-medium">Size</th>
                    {activeLeadsTab === 'sent' && (
                      <th className="text-left p-4 font-medium">Status</th>
                    )}
                    {(activeLeadsTab === 'available' || activeLeadsTab === 'notsent') && (
                      <th className="text-left p-4 font-medium">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {recommendationsLoading && activeLeadsTab === 'available' ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                          <span className="ml-2 text-gray-500">Loading recommendations...</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filterRecommendations(activeLeadsTab === 'available'
                        ? recommendations
                        : activeLeadsTab === 'sent'
                          ? recommendationsSent
                          : recommendationsHide)
                        .map((lead, index) => (
                          <tr
                            key={index}
                            className="border-b last:border-b-0 hover:bg-gray-50 transition-colors duration-200 ease-in-out cursor-pointer"
                            onClick={() => {
                              setSelectedProfile(lead);
                              setIsProfileDialogOpen(false);
                            }}
                          >
                            <td>
                              <div className='dashboard__table-company-logo'>
                                <img
                                  src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + lead.logo}
                                  alt={lead.company_name}
                                />
                              </div>
                            </td>
                            <td className="p-4 font-medium">{`${lead.first_name} ${lead.last_name}`}</td>
                            <td className="p-4">{lead.company_name}</td>
                            <td className="p-4">{lead.job_title}</td>
                            <td className="p-4">{lead.industry}</td>
                            <td className="p-4">{lead.company_size}</td>

                            {activeLeadsTab === 'sent' && (
                              <td className="p-4">
                                <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium min-w-[120px] ${(lead.demo_status === 'Approved' || lead.company_views > 0) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>

                                  {lead.company_views > 0 && (
                                    <>
                                      Viewed Profile
                                    </>
                                  )}

                                  {lead.company_views == 0 && (
                                    <>
                                      {lead.demo_status || 'Pending'}

                                    </>
                                  )}
                                </span>
                              </td>
                            )}

                            {(activeLeadsTab === 'available' || activeLeadsTab === 'notsent') && (
                              <td className="p-4">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInviteToDemo(lead);
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors min-w-[110px]"
                                  >
                                    <Send className="w-3 h-3 mr-1" />
                                    Send Intro
                                  </button>
                                  {activeLeadsTab === 'available' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBuyerNoMatch(lead.registered_user_id);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-xs font-medium transition-colors min-w-[110px]"
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Not a fit
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}

                      {(activeLeadsTab === 'available'
                        ? recommendations
                        : activeLeadsTab === 'sent'
                          ? recommendationsSent
                          : recommendationsHide).length === 0 && (
                          <tr>
                            <td colSpan={activeLeadsTab === 'available' ? 7 : 6} className="text-center py-8 text-gray-500">
                              {activeLeadsTab === 'available'
                                ? 'No recommendations available at this time'
                                : activeLeadsTab === 'sent'
                                  ? 'No invitations sent yet'
                                  : 'No leads marked as not a fit'}
                            </td>
                          </tr>
                        )}
                    </>
                  )}
                </tbody>
              </table>
              <ProfileDialog
                isOpen={isProfileDialogOpen}
                onClose={() => setIsProfileDialogOpen(false)}
                profile={selectedProfile}
              />

              {activeLeadsTab === 'available' && (
                <div className="mt-6">
                  {renderPaginationButtons()}
                  <div className="text-center mt-2 text-sm text-gray-500">
                    Showing {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} recommendations
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirmInvite}
        lead={selectedInvite}
      />
      <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default Recommendations;